/**
 * French (`fr`) string catalogue — mirrors the key shape of `./en.ts`. Brand
 * names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`, `Splash
 * Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; descriptive
 * copy translates into terse, gamey French.
 */

import type { Catalogue } from './en';

export const fr: Catalogue = {
  meta: {
    name: 'Français',
    code: 'FR',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULSE',
      role: 'Polyvalent neutre. Dégâts fiables partout sur le tracé.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Couplé au réseau. Plus fort avec un surplus de Pylons, plus faible en brownout.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Peintre de goulot. Petit rayon — ralentit et marque chaque ennemi à un point.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MORTIER',
      role: 'Tir indirect. Place-le LOIN du tracé — il a une zone morte au près.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Perforation en ligne de tir. Fort sur les lignes droites; faible dans les coins.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Alimentation. Construis-en assez pour couvrir la demande des tours (et un surplus pour Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'MUR',
      role: 'Bloqueur bon marché — mode Maze uniquement. Façonne la route des ennemis.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret : ton choix fiable — dégâts solides partout sur le tracé.',
    arc: 'Arc Coil : monte avec ton surplus d’énergie. Surconstruis des Pylons pour une chaîne plus forte.',
    cryo: 'Cryo Emitter : peinture serrée des goulots. Ralentit et marque les quelques cases au point de pincement.',
    mortar: 'Splash Mortar : tir indirect. Place-le LOIN du tracé — il ne peut pas tirer au près.',
    sniper: 'Sniper Rail : perforation en ligne de tir. Aligne-le sur une longue droite pour un effet maximal.',
    pylon: 'Power Pylon : alimente le réseau. Construis assez pour couvrir la demande des tours.',
    wall: 'Mur : bloqueur bon marché — façonne la route que doivent suivre les ennemis.',
  },

  hints: {
    build:
      'Construis une tour : choisis-en une en bas (ou appuie sur 1), puis clique sur une case lumineuse près du tracé.',
    placed: 'Bien. Ajoutes-en une autre, puis appuie sur Espace pour lancer la vague.',
    wave1: 'Les tours tirent toutes seules. Garde la barre d’énergie hors du rouge.',
    select: 'Clique sur une tour posée pour la sélectionner — U pour améliorer, S pour vendre.',
    brownout: 'Brownout ! Tours ralenties. Construis un Pylon (3) pour ajouter de l’énergie.',
  },

  contextHints: {
    overload:
      'Overload : monte la prochaine vague pour de plus grosses récompenses — à plus haut risque. Départ lent ? Pousse-le pour du Flux plus vite.',
    profileKey: 'Appuie sur P pour ouvrir ton Profil — le Flux débloque tours, capacités et modes.',
    endlessMod: 'Endless fait tourner les MODIFICATEURS de vague — adapte ta composition.',
    puzzleHover:
      'Mode Puzzle : niveaux logiques faits main avec murs bloquant la ligne de vue. Un tout autre jeu.',
  },

  hud: {
    buildZone: 'CONSTRUIRE',
    waveZone: 'VAGUE',
    abilitiesZone: 'CAPACITÉS',
    overloadZone: 'OVERLOAD',

    startWave: 'LANCER VAGUE',
    nextWave: 'VAGUE SUIV.',
    pause: 'PAUSE',
    resume: 'REPRENDRE',
    restart: 'RECOM.',
    deployHint: 'lancer [SPACE]',
    holdHint: 'pause [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'VAGUE SUIV.',

    audioOn: 'AUDIO ON',
    audioOff: 'AUDIO OFF',

    credits: 'CRÉDITS',
    core: 'NOYAU',
    wave: 'VAGUE',
    endless: 'ENDLESS',
    score: 'SCORE',
    powerGrid: 'RÉSEAU',

    runMode: 'MODE',
    difficulty: 'DIFFICULTÉ',
    profile: 'PROFIL',
    map: 'CARTE',
    clickHint: '▷ clic',

    locked: 'VERROUILLÉ',
    lockedShort: 'VERROU.',
    lockedTooltip:
      'VERROUILLÉ — débloque pour ✦{cost} Flux dans le Profil (appuie sur P).',
    lockedTowerNotice: '{tower} verrouillé',
    lockedAbility: 'Capacité verrouillée',
    lockedOverload: 'Palier Overload verrouillé',
    lockedMode: 'Mode {mode} verrouillé',
    lockedDifficulty: 'Hard verrouillé',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload s’applique dès la vague 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ ACTIF {sec}s',

    maxLevel: 'NIV. MAX',
    upgrade: 'AMÉLIORER L{level}',
    sell: 'VENDRE',
    target: 'CIBLE : {mode}',
    cycleTarget: 'changer · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "PHASE DE CONSTRUCTION  //  arme une tour [1·2·3·4·5], place-la, puis LANCE LA VAGUE [SPACE]",
    buildHintReinforce: "PHASE DE CONSTRUCTION  //  renforce la grille, puis LANCE LA VAGUE {wave} [SPACE]",
    mazeRoute: "R O U T E   D U   L A B Y",
    tiles: "CASES",
    abilityActive: "◉ ACTIF {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock ACTIVÉ",
    overclockNoticeOff: "{tower} Overclock DÉSACTIVÉ",

    tierMult: {
      hp: "PV",
      sp: "VT",
      cn: "NB",
      gd: "OR",
      sc: "PT",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'CAMPAGNE',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 vagues · victoire',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'choisir niveau',
  },
  modeBlurb: {
    campaign: '10 vagues. Boss en vague 10. Gagne et tu passes ton diplôme.',
    endless: 'Même carte. Un modificateur par vague. Adapte-toi ou tu coules.',
    maze: 'Champ ouvert. Tu traces la route avec des murs. Ralentis-les ou ronge leur butin.',
    puzzle: '10 niveaux faits main. Trouve la seule disposition gagnante.',
  },
  modeCard: {
    campaign: {
      tagline: 'Apprends la grille, terrasse le boss.',
      bullet1: '10 vagues réglées à la main + boss',
      bullet2: 'Tracé S fixe — bâtir, vague, améliorer',
      bullet3: 'La victoire ouvre Endless sur cette carte',
      length: '~8 min',
      skill: 'Travaille l’ordre de construction',
    },
    endless: {
      tagline: 'Adapte-toi ou ça fuit. Pas de fin.',
      bullet1: 'Joue la Campagne 1–10, puis monte sans fin',
      bullet2: 'Nouveau modificateur à chaque vague',
      bullet3: 'La partie s’arrête si le Noyau tombe',
      length: '~30 min',
      skill: 'Travaille les pivots longs',
    },
    maze: {
      tagline: 'Tu bâtis le labyrinthe qu’ils traversent.',
      bullet1: 'Champ ouvert — aucun chemin pré-tracé',
      bullet2: 'Tours et murs bon marché tracent la route',
      bullet3: 'Plus la route est longue, plus tu tires',
      length: '~12 min',
      skill: 'Travaille le routage spatial',
    },
    puzzle: {
      tagline: 'Une disposition gagne. Trouve-la.',
      bullet1: '10 niveaux, une seule solution',
      bullet2: 'Budget et vague fixes',
      bullet3: 'Murs de ligne de vue + limites de tours',
      length: '~2 min chacun',
      skill: 'Travaille la déduction',
    },
  },
  difficulty: {
    easy: 'FACILE',
    normal: 'NORMAL',
    hard: 'DIFFICILE',
  },
  difficultySub: {
    easy: 'doux',
    normal: 'équilibré',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUSE',
    pauseHint: 'APPUIE  ESPACE  POUR  REPRENDRE',

    missionComplete: '// MISSION ACCOMPLIE',
    gridSecured: 'RÉSEAU SÉCURISÉ',
    wavesRepelled: 'Les {total} vagues d’assaut ont été repoussées.',
    continueEndless: 'CONTINUER  →  ENDLESS',
    unlockEndless: 'débloque Endless dans le Profil pour continuer',
    restartCampaign: 'ou appuie sur  R  pour recommencer la campagne',

    systemFailure: '// PANNE SYSTÈME',
    coreBreached: 'NOYAU FORCÉ',
    endlessEnded: 'Course Endless terminée à la vague {wave}.',
    reactorOverrun: 'Réacteur submergé à la vague {wave} sur {total}.',
    runItBack: 'APPUIE  R  POUR  REJOUER',

    finalScore: 'SCORE FINAL',
    newHighScore: '★  NOUVEAU RECORD  ★',
    newEndlessBest: '★  NOUVEAU RECORD ENDLESS  ★',
    highScore: 'RECORD',
    endlessBest: 'RECORD ENDLESS',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// RÉPLIQUE — REMBOURSEMENT GRILLE  {sec}s //",
    overcharge: "// OVERCHARGE — GRILLE BOOSTÉE  {sec}s //",
    brownout: "// CHUTE GRILLE — TOURS BRIDÉES //",
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// MÉTA-PROGRESSION',
    closeHint: 'appuie sur  P  ou  ESC  pour fermer',
    tabUnlocks: 'DÉBLOCAGES',
    tabScoreboard: 'CLASSEMENT',
    flux: 'FLUX',
    unlockTree: 'ARBRE DE DÉBLOCAGE',
    unlockedCount: '{owned} / {total} DÉBLOQUÉS',
    lifetime: 'TOTAL',

    branchArsenal: 'ARSENAL',
    branchModes: 'MODES',
    branchChallenge: 'DÉFI',
    branchCosmetic: 'COSMÉTIQUE',

    statRunsPlayed: 'COURSES JOUÉES',
    statCampaignWins: 'VICTOIRES CAMPAGNE',
    statBestScore: 'MEILLEUR SCORE',
    statDeepestEndless: 'ENDLESS LE PLUS PROFOND',
    statTotalFlux: 'FLUX TOTAL',

    personalBests: 'RECORDS PERSONNELS',
    waveLabel: 'VAGUE {wave}',

    runHistory: 'HISTORIQUE',
    noRuns: 'Aucune course pour l’instant — termines-en une pour commencer la montée.',
    earlierRuns: '+ {n} course antérieure',
    earlierRunsPlural: '+ {n} courses antérieures',
    colRun: 'COURSE',
    colMode: 'MODE · DIFFICULTÉ',
    colResult: 'RÉSULTAT',
    colOverload: 'OVL',
    colScore: 'SCORE',
    colDeltaBest: 'Δ MEIL.',
    colFlux: 'FLUX',
    starBest: '★ MEIL.',

    statusUnlocked: 'DÉBLOQUÉ',
    statusOwned: 'POSSÉDÉ',
    statusRequiresPrereq: 'D’abord {prereq}',
    statusFluxShort: 'Il manque ✦{short} Flux',
    statusAffordable: 'Abordable — clique pour débloquer',
    statusCost: '✦{cost} pour débloquer',
    buyAction: 'ACHETER  ▸',
    nodeReq: 'REQ : {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'VOIR  CLASSEMENT',
    newPersonalBest: '★  NOUVEAU RECORD PERSO  ★',
    fromYourBest: 'DEPUIS TON MEILLEUR',

    grantTower: 'TOUR D’ARMEMENT',
    grantAbility: 'CAPACITÉ ACTIVE',
    grantMode: 'MODE DE JEU',
    grantDifficulty: 'DIFFICULTÉ',
    grantOverload: 'PALIER OVERLOAD {tier}',
    grantTheme: 'THÈME DE PLATEAU',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Tir indirect. Place-le DERRIÈRE le chemin — proximité = zone morte ; obus sur clusters, brise armure.",
      towerSniper: "Lance perforante en ligne. Visez une longue ligne droite — tous les ennemis dessus prennent ; faible en angles.",
      abilityAirstrike: "Frappe unique sur une case (45c). Nettoie un cluster ou grignote un gros.",
      abilityEmp: "Stun pan-champ (80c). Fige tous les ennemis un instant si la ligne saute.",
      modeEndless: "Joue au-delà de la vague 10 — vagues procédurales jusqu’à la chute du Cœur.",
      modeMaze: "Mode construction sur terrain ouvert. Les tours bloquent leur case, A*-pathing autour.",
      difficultyHard: "Vagues plus dures ; multiplicateurs or et score plus gros. Débloque la bande Flux haute.",
      overloadCritical: "Overload tier 3 — chauffe pour des multiplicateurs score/Flux plus gros à la prochaine vague.",
      overloadMeltdown: "Overload tier 4 — la vague pré-set la plus dure. Multiplicateurs top ; build solide requis.",
      themeAmber: "Palette cosmétique — terminal CRT amber chaud. Purement cosmétique ; pas d’effet.",
      themePhosphor: "Palette cosmétique — vert phosphore vintage. Purement cosmétique ; pas d’effet.",
    },
  },

  help: {
    eyebrow: '/ /  R É F É R E N C E',
    title: 'MANUEL D’OPÉRATION',
    controls: 'COMMANDES',
    powerGrid: 'LE RÉSEAU',
    powerGridBody:
      'Les armes consomment de l’énergie ; les Pylons en fournissent. Quand la demande dépasse l’offre, le réseau tombe en brownout et toutes les tours ralentissent — construis des Pylons pour continuer à tirer.',
    objective: 'OBJECTIF',
    objectiveBody:
      'Nettoie les 15 vagues de la campagne pour gagner ; tu perds dès que les vies du Noyau atteignent zéro. Le mode Endless tourne à l’infini.',
    mazeMode: 'MODE MAZE',
    mazeModeBody:
      'Un troisième mode en champ ouvert : les tours bloquent leur case et les ennemis les contournent en A*, donc tu construis le labyrinthe. Tu ne peux pas barricader le Noyau.',
    glitches: 'GLITCHES',
    towers: 'TOURS',
    abilities: 'CAPACITÉS',
    replayIntro: '↺  REJOUER INTRO',
    closeHint: 'Esc / H / ? ou clic n’importe où pour fermer',
  },

  intro: {
    eyebrow: '/ /  B R I E F I N G   S Y S T È M E',
    titleBig: 'OVERVOLT',
    subtitle: 'Défends le Noyau',
    line1:
      'Des programmes corrompus avancent dans le circuit vers ton Noyau. Construis des tours pour les détruire avant qu’ils ne percent.',
    line2:
      '⚡ Chaque tour consomme de l’énergie. Si tu sur-construis, le réseau tombe en brownout — toutes les tours ralentissent d’un coup. Construis des Pylons pour garder le courant.',
    line3: 'Construis pendant la phase calme, puis appuie sur Lancer Vague. Survis à 15 vagues pour gagner.',
    line4: 'Souris construit · 1–5 choix · Espace start/pause · U améliorer · S vendre',
    gotIt: 'COMPRIS  →',
    gotItHint: 'appuie sur une touche pour continuer',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Première Lumière',
        hint: 'Pose un Pulse à côté du couloir. N’importe quelle case à portée fait l’affaire.',
      },
      'p1-long-lance': {
        name: 'Longue Lance',
        hint: 'Un Sniper aligné sur la voie perfore chaque ennemi.',
      },
      'p2-over-the-wall': {
        name: 'Par-dessus le Mur',
        hint: 'Le mur bloque tout rayon direct. Les obus de mortier passent en cloche.',
      },
      'p3-funhouse-mirror': {
        name: 'Miroir Déformant',
        hint: 'Un Sniper enfilant la fente touche les deux voies.',
      },
      'p4-surplus-riches': {
        name: 'Richesses en Surplus',
        hint: 'Sur-construis les Pylons au-delà de la zone libre. L’Arc Coil prospère sur le surplus.',
      },
      'p5-pinch-point': {
        name: 'Point de Pincement',
        hint: 'Le Cryo peint le goulot ; les Pulses autour achèvent le groupe.',
      },
      'p6-cipher-gauntlet': {
        name: 'Gantelet Cipher',
        hint: 'Le mortier déchire l’armure ; le Cryo peint le coude ; le Sniper perfore le segment du bas. Utilise tout l’arsenal.',
      },
      'p7-kernel-echo': {
        name: 'Écho du Kernel',
        hint: 'Les mortiers passent par-dessus les murs de LOS. Le Sniper enfile par le bas. Surveille le Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Crise Overclock',
        hint: 'Un seul Pulse ne suffit pas. Overclock la fenêtre critique — la cadence soutenue ne suffit pas.',
      },
      'p9-pylon-pinch': {
        name: 'Pincement des Pylons',
        hint: 'Regroupe les Pylons entre tes tours — la limite d’aura récompense une zone serrée.',
      },
    },
    briefing: {
      eyebrow: "BRIEFING PUZZLE",
      startBtn: "Lancer ▷",
      cancelBtn: "Annuler",
      sectionConstraints: "CONTRAINTES",
      sectionTowers: "TOURS",
      sectionWinCondition: "CONDITION DE VICTOIRE",
      budgetLabel: "Budget d’or",
      wavesLabel: "Vagues",
      towerAllowed: "AUTORISÉE",
      towerForbidden: "VERROUILLÉE",
      resultEyebrowWon: "PUZZLE RÉSOLU",
      resultEyebrowLost: "PUZZLE RATÉ",
      resultTitleWon: "RÉSOLU !",
      resultTitleLost: "RÉESSAYE",
      resultStatTowers: "TOURS",
      resultStatGold: "OR DÉPENSÉ",
      resultStatLives: "VIES RESTANTES",
      resultNextBtn: "Niveau suivant ▷",
      resultReplayBtn: "Rejouer",
      resultResetBtn: "Réinitialiser",
      resultBackBtn: "Retour à la sélection",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Niveaux logiques faits main — devine la seule disposition gagnante.",
      close: "Fermer",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 VERROUILLÉ",
      solved: "✓ Résolu",
      unsolved: "▷ Non résolu",
      noSolve: "Pas encore résolu",
      bestLine: "Meilleur : {towers}t · {gold}g · {power}p",
      allowed: "Autorisé : {list}",
      parPlaceholder: "par : — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'LANGUE',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Reprendre la course précédente ?',
    resume: 'Reprendre',
    newRun: 'Nouvelle',
    hint: '[Enter] Reprendre   ·   [Esc] Nouvelle',
    detail: "{mode} · {difficulty} · vague {wave} · {towers} tours · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  LE KERNEL",
    phase: "PHASE {phase} · {name}",
    powerDrain: "⚡ DRAIN ÉNERGIE",
    kernelIncoming: "⚠  KERNEL  ARRIVE  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Courbe en S ouverte — l’agencement OVERVOLT classique." },
    cramped: { label: "Switchyard", tagline: "Voies étroites — chaque case est disputée." },
    lines: { label: "Long Lines", tagline: "Longues lignes droites — dirige la lance." },
    serpentine: { label: "Snake Run", tagline: "Serpentin serré — le Cryo painter brille." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominal",
      charged: "Chargé",
      surging: "Surtension",
      critical: "Critique",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Charge Blindée" },
    fastPack: { label: "Meute Rapide" },
    scatteredSpread: { label: "Dispersion" },
    streakRunners: { label: "Coureurs Streak" },
    daemonRush: { label: "Ruée Daemon" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Chair à canon — peu cher, lent, fragile.",
    packet: "Rapide et faible ; il fonce vers le Cœur.",
    daemon: "Éponge à HP ; frappe fort à une fuite.",
    fork: "Se sépare en deux Bits à la mort.",
    cipher: "Blindé — déchire-le avec la Corrosion du Mortier.",
    patch: "Soigne les Glitches voisins ; abats-le en premier.",
    buffer: "Bouclier régénérant ; perce-le en burst.",
    streak: "Sprinte seul ; résiste au slow. Couvre le chemin.",
    kernel: "Boss de phase — draine ton énergie. Burst-le avant la phase suivante.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Boost la grille : chaque tour tire plus vite un court instant. À planifier sur une grosse vague.",
    airstrike: "Frappe unique sur une case ciblée. Nettoie un cluster ou grignote un gros.",
    empBurst: "Stun pan-champ — tous les ennemis figent un instant. Achète du temps si la ligne saute.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} crédits",
    next: "SUIVANT  →  LV {level}   {cost} crédits",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "ALIMENTATION  +{n}w à la grille",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    CHAÎNE {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} cibles",
      slow: "SLOW ×{factor}  pour  {sec}s",
      brittle: "FRAGILITÉ +{pct}% dmg des autres tours",
      pwrBlast: "PWR {power}w    BLAST {tiles} cases",
      pwrSingle: "PWR {power}w    1 cible",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Arme Pulse / Arc / Pylon / Cryo / Mortier / Sniper",
    mouse: "Place sur une case · clique sur une tour pour sélectionner",
    space: "Lance la vague · met en pause",
    fast: "Avance rapide (1× ⇄ 2×)",
    upgradeSell: "Améliore / vends la tour sélectionnée",
    target: "Change le mode de ciblage de la tour sélectionnée",
    profile: "Ouvre le Profil (arbre · classement)",
    utility: "Annule · sourdine · redémarre la course",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Mode Endless" },
    modeMaze: { name: "Mode Maze" },
    difficultyHard: { name: "Difficulté Hard" },
    overloadCritical: { name: "Overload : Critique" },
    overloadMeltdown: { name: "Overload : Meltdown" },
    themeAmber: { name: "Terminal Amber" },
    themePhosphor: { name: "Vert Phosphore" },
  },

  // Sprint 73 — tutoriel interactif (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'LANCER LE TUTORIEL',
      startSub: 'Première fois ? Visitez le tour →',
      replay: 'RELANCER LE TOUR',
      replaySub: 'Réviser les bases',
    },
    step: {
      welcome: {
        title: 'Bienvenue dans OVERVOLT',
        body: "Vous êtes l'IA défensive d'un réacteur défaillant. Des programmes corrompus marchent sur le circuit vers votre Core — détruisez-les avant qu'ils ne percent.",
      },
      path: {
        title: 'Le chemin',
        body: 'Les ennemis suivent toujours cette courbe en S, de la case spawn à gauche jusqu’au Core à droite. Vous décidez où planter vos tourelles.',
      },
      firstPlace: {
        title: 'Placez votre première tourelle',
        body: 'Une Pulse Turret coûte 50 crédits et consomme 4 d’énergie. Cliquez sur la case lumineuse à côté du chemin.',
      },
      wave1: {
        title: 'Lancez la première vague',
        body: 'Appuyez sur LANCER VAGUE. Votre Pulse tire automatiquement sur tout ce qui est à portée.',
      },
      towerRoles: {
        title: 'Cinq tours, cinq rôles',
        body: 'Pulse : fiable partout. Sniper : longues lignes droites. Mortar : tir indirect, EN ARRIÈRE du chemin. Arc : monte avec le surplus d’énergie. Cryo : peint un goulot.',
      },
      powerGrid: {
        title: 'Le réseau électrique',
        body: 'Chaque tour consomme. Quand la demande dépasse l’offre, le réseau s’effondre et toutes les tours ralentissent. Construisez des Pylons — les voisins amplifient la cadence.',
      },
      overloadDial: {
        title: 'Overload — risque vs récompense',
        body: 'Tournez la molette vers le haut pour la prochaine vague : ennemis plus durs, plus de Flux. Baissez-la pour jouer prudemment.',
      },
      overclock: {
        title: 'Surcadencer une tour',
        body: 'Activez OVERCLOCK sur une tour sélectionnée pour une fenêtre burst : tir plus rapide, plus de consommation. Burst gagnant, perte sur la durée.',
      },
      abilities: {
        title: 'Capacités actives',
        body: 'Trois emplacements : Overcharge booste tout le réseau. Airstrike pose une zone brûlée durable. EMP Burst étourdit tout. Planifiez — ne paniquez pas.',
      },
      mazePreview: {
        title: 'Mode Maze',
        body: 'Pas de chemin fixe — les tours bloquent leurs cases et les ennemis A*-routent autour. Vous construisez l’itinéraire qu’ils doivent emprunter.',
      },
      puzzlePreview: {
        title: 'Mode Puzzle',
        body: 'Niveaux logiques avec murs en ligne de vue. Budget fixe, vague fixe — trouvez la seule disposition gagnante. Un jeu différent.',
      },
      wrapUp: {
        title: 'Vous êtes prêt',
        body: 'Les runs rapportent du FLUX — dépensez-le dans le Profil pour débloquer tours, capacités et modes. Commencez par Campaign ; revenez pour Endless, Maze et Puzzle.',
      },
    },
    ui: {
      eyebrow: 'ÉTAPE {n} / {total}',
      next: 'SUIVANT →',
      back: '← RETOUR',
      skip: 'PASSER LE TOUR',
      play: 'JOUER  ▸',
      skipConfirm: 'Passer le tour ? Vous pourrez le relancer à tout moment depuis l’écran titre.',
    },
  },
};
